import { useQuery } from "@apollo/client/react"
import Alert from "@mui/material/Alert"
import { DateTime } from "luxon"
import React, { useEffect, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"

import CalendarView from "@/components/calendar_view"
import Layout from "@/components/layout"
import EditForm from "@/components/reservations/edit_form"
import Navigation from "@/components/reservations/navigation"
import { useVenue } from "@/contexts/venue"
import {
  GetOpeningHoursDocument,
  type GetOpeningHoursQuery,
  type GetOpeningHoursQueryVariables,
  ReservationDateField,
  ReservationStatus,
} from "@/graphql/graphql"
import { useReservations } from "@/graphql/hooks"
import { useReservationTabHydrate } from "@/shared/hooks"
import { useStore } from "@/store"
import { getOpeningHoursForDate, getEffectiveCalendarRange } from "@/utils/opening_hours"

const ReservationsPage = () => {
  const { venue } = useVenue()
  const { reservationId } = useParams()

  useReservationTabHydrate(venue)

  const navigate = useNavigate()

  useEffect(() => {
    if (!venue.permissions?.viewReservations) {
      navigate(`/panel/${venue.slug}/settings`)
    }
  }, [venue.permissions?.viewReservations, venue.slug, navigate])

  const { currentDate } = useStore()
  const { reservations, loading, error } = useReservations({
    dateFilter: {
      field: ReservationDateField.ReservationDate,
      from: currentDate,
      to: currentDate,
    },
  })

  const calendarReservations = useMemo(() => {
    if (!reservations) return []
    return reservations.filter(
      (reservation) =>
        reservation.status !== ReservationStatus.Cancelled &&
        reservation.status !== ReservationStatus.NoShow &&
        reservation.status !== ReservationStatus.Removed,
    )
  }, [reservations])

  const { data: openingHoursData } = useQuery<GetOpeningHoursQuery, GetOpeningHoursQueryVariables>(
    GetOpeningHoursDocument,
    {
      variables: { slug: venue.slug },
    },
  )

  const openingHours = useMemo(
    () => getOpeningHoursForDate(openingHoursData?.venue?.openingHours, currentDate),
    [openingHoursData?.venue?.openingHours, currentDate],
  )

  const reservationsWithDateTime = useMemo(() => {
    if (!calendarReservations) return []
    return calendarReservations.map((r) => ({
      startTime: DateTime.fromISO(r.startTime),
      endTime: DateTime.fromISO(r.endTime),
    }))
  }, [calendarReservations])

  const { effectiveStartHour, effectiveEndHour, openingStartHour, openingEndHour, closed } = useMemo(
    () => getEffectiveCalendarRange(openingHours, reservationsWithDateTime),
    [openingHours, reservationsWithDateTime],
  )

  return (
    <Layout title="Reservations" flex data-testid="panel-reservations-page">
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      ) : null}

      <Navigation />

      <CalendarView
        reservations={calendarReservations}
        loading={loading}
        startHour={effectiveStartHour}
        endHour={effectiveEndHour}
        openingStartHour={openingStartHour}
        openingEndHour={openingEndHour}
        closed={closed}
      />

      <EditForm venue={venue} reservationId={reservationId} onClose={() => navigate("../../", { relative: "path" })} />
    </Layout>
  )
}

export default ReservationsPage
